//Solar Report - v2
//Pure lookups over the site defaults payload (reportParameters.EndUses).
//No Power BI dependencies so they can be unit-tested on their own.
//
//Payload shape:
//[{ DefaultSiteName, DefaultArrayName, Arrays: [{ ArrayName, InverterName }] }]

/**
 * Parses the site defaults from the report parameters. Accepts a JSON string
 * or an already parsed array; anything else yields an empty list.
 * @param {String|Array} raw - The EndUses report parameter.
 * @returns {Array}
 */
export const parseSiteDefaults = (raw) => {
  if (!raw) return [];

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Solar v2: could not parse site defaults', error);
    return [];
  }
};

/**
 * Finds the defaults entry of a site (exact match).
 * @param {Array} siteDefaults - The parsed site defaults.
 * @param {String} site - The site name.
 * @returns {Object|null}
 */
export const getSiteEntry = (siteDefaults, site) =>
  siteDefaults.find((entry) => entry.DefaultSiteName === site) || null;

/**
 * Gets the inverter to select for an array of a site.
 * @param {Array} siteDefaults - The parsed site defaults.
 * @param {String} site - The site name.
 * @param {String} array - The array name.
 * @returns {String|null} - The inverter name, or null if not found.
 */
export const getInverterForArray = (siteDefaults, site, array) => {
  const entry = getSiteEntry(siteDefaults, site);
  const match = entry?.Arrays?.find((item) => item.ArrayName === array);

  return match?.InverterName ?? null;
};

/**
 * Gets the default selections of a site.
 * @param {Array} siteDefaults - The parsed site defaults.
 * @param {String} site - The site name.
 * @returns {{ arrayDetailsArray: String, invertersArray: String, inverter: String }|null}
 *   null when the site has no entry (single solar level, no arrays).
 */
export const getSiteSelections = (siteDefaults, site) => {
  const entry = getSiteEntry(siteDefaults, site);

  if (!entry) return null;

  const first = entry.Arrays?.[0];

  return {
    arrayDetailsArray: entry.DefaultArrayName ?? null,
    invertersArray: first?.ArrayName ?? null,
    inverter: first?.InverterName ?? null,
  };
};
